import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Briefcase, 
  Truck, 
  FlaskConical, 
  Microscope, 
  Package, 
  Calculator, 
  Users,
  ChevronRight
} from 'lucide-react';

export const ServicesSection = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      id: 'pmo',
      icon: Briefcase,
      title: 'Project Management Office (PMO)',
      description: 'Complete project lifecycle management',
      features: [
        'Manage RFPs, proposals, and budgets',
        'Execute projects with milestone tracking',
        'Ensure timely completion'
      ],
      image: 'https://images.pexels.com/photos/8284724/pexels-photo-8284724.jpeg'
    },
    {
      id: 'scm',
      icon: Truck,
      title: 'Supply Chain Management (SCM)',
      description: 'Streamlined procurement and vendor management',
      features: [
        'Raw material requests with approval flows',
        'Streamlined purchase orders and vendor communication',
        'Integrated procurement and quality checks'
      ],
      image: 'https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjaGVtaWNhbCUyMGluZHVzdHJ5fGVufDB8fHx8MTc1NzkwNTIyMnww&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'chemistry',
      icon: FlaskConical,
      title: 'Chemistry & Formulations',
      description: 'Advanced recipe and batch management',
      features: [
        'Recipe and formulation management',
        'Batch creation with traceability',
        'Controlled process adjustments and observations'
      ],
      image: 'https://images.pexels.com/photos/356040/pexels-photo-356040.jpeg'
    },
    {
      id: 'analytical',
      icon: Microscope,
      title: 'Analytical Services',
      description: 'Comprehensive testing and quality assurance',
      features: [
        'Manage test samples, QA, and reporting',
        'Approval workflows for analytical reports',
        'Full audit-trail compliance'
      ],
      image: 'https://images.unsplash.com/photo-1669101602104-bfa264a17cce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxwaGFybWFjZXV0aWNhbCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzU3OTA1MjEyfDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'warehouse',
      icon: Package,
      title: 'Warehouse & Inventory',
      description: 'Smart inventory and quality management',
      features: [
        'Vendor master & inventory tracking',
        'Material quality checks',
        'Inventory check-in/check-out for projects'
      ],
      image: 'https://images.unsplash.com/photo-1629447388369-760612337eff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxjaGVtaWNhbCUyMGluZHVzdHJ5fGVufDB8fHx8MTc1NzkwNTIyMnww&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'finance',
      icon: Calculator,
      title: 'Finance & Accounting',
      description: 'Complete financial management solution',
      features: [
        'Vendor bills, invoices, credit/debit notes',
        'Budget control & project-specific financials',
        'Monthly, quarterly, and annual reports'
      ],
      image: 'https://images.unsplash.com/photo-1754548930550-be9fa88874f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGludGVyZmFjZXxlbnwwfHx8fDE3NTc5MDUyMTd8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'hrms',
      icon: Users,
      title: 'HRMS',
      description: 'Complete human resource management',
      features: [
        'Employee management, payroll, and leave systems',
        'Configurable approval flows',
        'Built-in helpdesk and notifications'
      ],
      image: 'https://images.unsplash.com/photo-1754548930550-be9fa88874f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGludGVyZmFjZXxlbnwwfHx8fDE3NTc5MDUyMTd8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-4">
            Core Services & Modules
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive ERP Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Integrated modules designed specifically for pharmaceutical, chemical, and analytical industries
          </p>
        </div>

        {/* Services Tabs */}
        <Tabs defaultValue="pmo" className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 h-auto p-2 bg-gray-100 rounded-xl mb-12">
            {services.map((service) => (
              <TabsTrigger
                key={service.id}
                value={service.id}
                className="flex flex-col items-center p-4 data-[state=active]:bg-white data-[state=active]:text-teal-600 rounded-lg transition-all duration-200"
              >
                <service.icon className="h-6 w-6 mb-2" />
                <span className="text-xs font-medium text-center leading-tight">{service.title.split(' ')[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Tab Contents */}
          {services.map((service) => (
            <TabsContent key={service.id} value={service.id} className="mt-0">
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Left - Image */}
                  <div className="relative h-64 lg:h-auto">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-900/50 to-transparent"></div>
                  </div>

                  {/* Right - Content */}
                  <CardContent className="p-8 lg:p-12">
                    <div className="flex items-center mb-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 rounded-lg mr-4">
                        <service.icon className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                        <p className="text-teal-600 font-medium">{service.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <ChevronRight className="h-5 w-5 text-teal-600 mt-0.5 mr-3 flex-shrink-0" />
                          <span className="text-gray-700 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <div className="text-sm text-gray-500 mb-2">Key Benefits</div>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm rounded-full">Automated Workflows</span>
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm rounded-full">Real-time Tracking</span>
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm rounded-full">Compliance Ready</span>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};